import {
    type FC,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef
} from 'react'

import { produce } from 'immer'

import {
    debounce,
    useSelfUpdatingRef
} from '../helpers/hooks'
import { apiVideoPlayedTime } from '../services/endpoints/tracking'
import './_modal.scss'

const initialState = {
    impressionIds: {}
}

enum TRACKING_ACTION_TYPES {
    ADD_IMPRESSION_ID,
    CLEAR_IMPRESSION_IDS
}

type IImpressionIds = Record<string, boolean>

interface IState {
    impressionIds: IImpressionIds
}

interface IAction<Type, State> {
    type: Type
    payload?: Partial<State>
}

export interface VideoPlayedPayload {
    video_id: string
    total_watch_seconds: number
    unique_watch_seconds: number
    completion_rate: number
    video_duration_seconds: number
    is_preview: boolean
}

interface IValue {
    addImpressionId: (id: string) => void
    clearImpressionIds: () => void
    hasImpressionId: (id: string) => boolean
    addOrEditQueuedPlayedS: (
        playedSObj: VideoPlayedPayload
    ) => void
    sendQueuedPlayedS: () => void
}

export const TrackingContext = createContext<IValue>(
    undefined as unknown as IValue
)

const trackingReducer = (
    state: IState,
    action: IAction<
        TRACKING_ACTION_TYPES,
        IState
    >
): IState => {
    const { type, payload } = action

    switch (type) {
        case TRACKING_ACTION_TYPES.ADD_IMPRESSION_ID:
            return {
                ...state,
                impressionIds: {
                    ...state.impressionIds,
                    ...payload?.impressionIds
                }
            }

        case TRACKING_ACTION_TYPES.CLEAR_IMPRESSION_IDS:
            return {
                ...state,
                impressionIds: {}
            }

        default:
            throw new Error(
                'Unhandled action in videosReducer.'
            )
    }
}

// VIDEO TRACKING

interface IVideosState {
    playedSQueued: Record<
        string,
        VideoPlayedPayload
    >
}

const initialVideosState: IVideosState = {
    playedSQueued: {}
}

enum VIDEOS_ACTION_TYPES {
    QUEUE_PLAYED_S,
    CLEAR_QUEUED_PLAYED_S
}

type IVideoAction =
    | {
          type: VIDEOS_ACTION_TYPES.QUEUE_PLAYED_S
          payload: VideoPlayedPayload
      }
    | {
          type: VIDEOS_ACTION_TYPES.CLEAR_QUEUED_PLAYED_S
          payload?: never
      }

const getPlayedQueueKey = (
    payload: VideoPlayedPayload
): string => {
    return `${payload.video_id}:${
        payload.is_preview ? 'preview' : 'full'
    }`
}

const videosReducer = (
    state: IVideosState,
    action: IVideoAction
): IVideosState => {
    const { type } = action

    switch (type) {
        case VIDEOS_ACTION_TYPES.QUEUE_PLAYED_S:
            return produce(state, draft => {
                const payload = action.payload

                const queueKey =
                    getPlayedQueueKey(payload)

                const oldQueued =
                    draft.playedSQueued[queueKey]

                if (!oldQueued) {
                    draft.playedSQueued[queueKey] = {
                        ...payload
                    }

                    return
                }

                /*
                 * Keep the strongest/latest snapshot for this
                 * video and playback type.
                 *
                 * We use max rather than adding because each
                 * hook payload already represents the complete
                 * tracking session snapshot.
                 */
                oldQueued.total_watch_seconds =
                    Math.max(
                        oldQueued.total_watch_seconds,
                        payload.total_watch_seconds
                    )

                oldQueued.unique_watch_seconds =
                    Math.max(
                        oldQueued.unique_watch_seconds,
                        payload.unique_watch_seconds
                    )

                oldQueued.completion_rate =
                    Math.max(
                        oldQueued.completion_rate,
                        payload.completion_rate
                    )

                oldQueued.video_duration_seconds =
                    Math.max(
                        oldQueued.video_duration_seconds,
                        payload.video_duration_seconds
                    )

                oldQueued.is_preview =
                    payload.is_preview
            })

        case VIDEOS_ACTION_TYPES.CLEAR_QUEUED_PLAYED_S:
            return {
                ...state,
                playedSQueued: {}
            }

            default:
                throw new Error(
                    'Unhandled action in videosReducer.'
                )
    }
}

export const TrackingProvider: FC<{
    children?: React.ReactNode
}> = ({ children }) => {
    const [state, dispatch] = useReducer(
        trackingReducer,
        initialState
    )

const addImpressionId = useCallback(
    (id: string) => {
        dispatch({
            type:
                TRACKING_ACTION_TYPES.ADD_IMPRESSION_ID,
            payload: {
                impressionIds: {
                    [id]: true
                }
            }
        })
    },
    []
)

    const hasImpressionId = useCallback(
        (id: string) => {
            return Boolean(
                state.impressionIds[id]
            )
        },
        [state.impressionIds]
    )

    const clearImpressionIds =
        useCallback(() => {
            dispatch({
                type:
                    TRACKING_ACTION_TYPES.CLEAR_IMPRESSION_IDS
            })
        }, [])


    const [videosState, videosDispatch] =
        useReducer(
            videosReducer,
            initialVideosState
        )

    const addOrEditQueuedPlayedS =
        useCallback(
            (
                playedSObj:
                    VideoPlayedPayload
            ) => {
                videosDispatch({
                    type:
                        VIDEOS_ACTION_TYPES.QUEUE_PLAYED_S,
                    payload: playedSObj
                })
            },
            []
        )

const sendQueuedPlayedS = useCallback(() => {
    const videosPlayed = Object.values(
        videosState.playedSQueued
    )

    if (!videosPlayed.length) {
        return
    }

    videosDispatch({
        type: VIDEOS_ACTION_TYPES.CLEAR_QUEUED_PLAYED_S
    })

    const videoId = videosPlayed[0].video_id

    const payload = videosPlayed.map(
        ({ video_id: _videoId, ...stats }) => stats
    )

    void apiVideoPlayedTime(
        videoId,
        payload
    )
}, [videosState.playedSQueued])

    const sendQueuedPlayedSRef =
        useSelfUpdatingRef(
            sendQueuedPlayedS
        )

    const debounceSendPlayedSRef =
        useRef(
            debounce(() => {
                sendQueuedPlayedSRef.current()
            }, 2000)
        )

    useEffect(() => {
        if (
            Object.keys(
                videosState.playedSQueued
            ).length > 0
        ) {
            debounceSendPlayedSRef.current()
        }
    }, [
        videosState.playedSQueued
    ])

    useEffect(() => {
        return () => {
            sendQueuedPlayedSRef.current()
        }
    }, [
        sendQueuedPlayedSRef
    ])

    const value: IValue = useMemo(
        () => ({
            addImpressionId,
            clearImpressionIds,
            hasImpressionId,
            addOrEditQueuedPlayedS,
            sendQueuedPlayedS
        }),
        [
            addImpressionId,
            clearImpressionIds,
            hasImpressionId,
            addOrEditQueuedPlayedS,
            sendQueuedPlayedS
        ]
    )

    return (
        <TrackingContext.Provider
            value={value}
        >
            {children}
        </TrackingContext.Provider>
    )
}

export const useTrackingContext = () => {
    const context =
        useContext(TrackingContext)

    if (context === undefined) {
        throw new Error(
            'useTrackingContext must be used within TrackingContext'
        )
    }

    return context
}